// «Отправить обновление» и «Отменить правки» черновика живого курса (контракт §3a).
// Все три ручки — по ЖИВОМУ id: `course.id` в редакторе и есть живой id. Правило заявки,
// отказов и отмены — общее со статьёй (`moderation/lib/draftUpdate.ts`).
import { useState } from 'react'
import type { ValidationError } from '@/types/api'
import { useStore } from 'react-redux'
import type { RootState } from '@/store'
import { useAppDispatch } from '@/store/hooks'
import {
  useDiscardCourseDraftMutation,
  useLazyGetUpdateImpactQuery,
  useSubmitCourseUpdateMutation,
} from '@/features/moderation/moderationApi'
import { useCourseModeration } from '@/features/moderation/useCourseModeration'
import { parseModerationError } from '@/features/moderation/lib/parseModerationError'
import { handleUpdateFailure } from '@/features/moderation/lib/updateFailure'
import { discardDraft, sendDraftUpdate } from '@/features/moderation/lib/draftUpdate'
import { useGuardedTask } from '@/features/moderation/lib/useGuardedTask'
import { refetchCourseModeration } from '@/features/moderation/lib/refetchCourseModeration'
import type { UpdateImpact } from '@/features/moderation/types'
import { useRetryCountdown } from './useRetryCountdown'
import { messagesNow } from '@/i18n'
import { messages } from './messages'

export const useCourseUpdate = (courseId: number) => {
  const [fetchImpact] = useLazyGetUpdateImpactQuery()
  const [submitUpdate] = useSubmitCourseUpdateMutation()
  const [discardCourseDraft] = useDiscardCourseDraftMutation()
  const dispatch = useAppDispatch()
  const store = useStore<RootState>()
  const { refetch } = useCourseModeration(courseId)
  const countdown = useRetryCountdown()
  const [impact, setImpact] = useState<UpdateImpact | null>(null)
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([])
  const { guarded, isBusy } = useGuardedTask()

  const handlers = { onValidation: setValidationErrors, refetch, startCountdown: countdown.start }

  const send = () =>
    sendDraftUpdate({
      request: () => submitUpdate(courseId).unwrap(),
      doneText: () => messagesNow(messages).updateSent,
      refresh: () => refetchCourseModeration(dispatch, store.getState, courseId),
      ...handlers,
    })

  const requestSubmit = () =>
    guarded(async () => {
      setValidationErrors([])
      try {
        const next = await fetchImpact(courseId).unwrap()
        // Прогресс учеников после одобрения не вернуть — без подтверждения заявку не шлём.
        if (next.affectedLearners > 0) {
          setImpact(next)
          return
        }
      } catch (err) {
        handleUpdateFailure(parseModerationError(err), handlers)
        return
      }
      await send()
    })

  const confirmSubmit = () =>
    guarded(async () => {
      setImpact(null)
      await send()
    })

  const discard = () =>
    guarded(() => discardDraft(() => discardCourseDraft(courseId).unwrap(), refetch))

  return {
    requestSubmit,
    impact,
    confirmSubmit,
    cancelImpact: () => setImpact(null),
    discard,
    isBusy,
    retryLabel: countdown.label,
    validationErrors,
    clearValidationErrors: () => setValidationErrors([]),
  }
}
